import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import OCGatewayClient from "../client/OCGatewayClient";
import { RfidLastSeen } from "../models/RfidLastSeen";
import type { RfidLastSeenLocation } from "../models/RfidLastSeen";
import {
    gatewayRealtimeQueryOptions,
    getNumberQueryKeyTail,
    updateMatchingQueries
} from "./gatewayQueryUtils";

type RfidLastSeenUpdateBody = Partial<RfidLastSeen> & Record<string, any>;

type RfidLastSeenUpdate = {
    type: 'create' | 'update' | 'delete';
    deviceId: string;
    rfidCode: string;
    timestamp: Date | string;
    body?: RfidLastSeenUpdateBody;
};

type HideRfidLastSeenInput = {
    deviceId: string;
    rfidCode: string;
    eventId: number;
};

type HideRfidLastSeenResponse = {
    code: 200;
    hidden: true;
};

type SetRfidLastSeenLocationInput = {
    deviceId: string;
    rfidCode: string;
    location: RfidLastSeenLocation;
};

type SetRfidLastSeenLocationResponse = {
    code: 200;
    updated: true;
    rfidLastSeen: RfidLastSeen;
};

type GatewayError = {
    code?: number;
    message?: string;
};

const rfidLastSeenQueryKey = 'getRfidLastSeen';
const rfidLastSeenByDeviceQueryKey = 'getRfidLastSeenByDevice';

let subscribed = false;

const getSortTimestampMs = (item: RfidLastSeen) => {
    return item.locationUpdatedAt?.getTime() ?? item.eventTimestamp?.getTime() ?? item.updatedAt?.getTime() ?? 0;
};

const sortRfidLastSeen = (items: RfidLastSeen[]) => {
    return [...items].sort((left, right) => getSortTimestampMs(right) - getSortTimestampMs(left));
};

const getDeviceItemKey = (item: Pick<RfidLastSeen, 'deviceId' | 'rfidCode'>) => `${item.deviceId}:${item.rfidCode}`;

const normalizeItems = (items: RfidLastSeen[]) => {
    return items.map((item) => new RfidLastSeen(item));
};

const dedupeItems = (
    items: RfidLastSeen[],
    getKey: (item: RfidLastSeen) => string
) => {
    const itemsByKey = new Map<string, RfidLastSeen>();

    for (const item of sortRfidLastSeen(items)) {
        const key = getKey(item);

        if (!itemsByKey.has(key)) {
            itemsByKey.set(key, item);
        }
    }

    return Array.from(itemsByKey.values());
};

const applyLimit = (items: RfidLastSeen[], limit?: number | null) => {
    return typeof limit === 'number' ? items.slice(0, limit) : items;
};

export const isRfidLastSeenHidden = (item: Pick<RfidLastSeen, 'eventId' | 'hiddenAt' | 'hiddenEventId'>) => {
    return !!item.hiddenAt && item.hiddenEventId === item.eventId;
};

const applyRfidLastSeenUpdate = (
    items: RfidLastSeen[] | undefined,
    update: RfidLastSeenUpdate,
    getKey: (item: Pick<RfidLastSeen, 'deviceId' | 'rfidCode'>) => string,
    limit?: number | null
) => {
    if (!items) {
        return items;
    }

    const updateKey = getKey(update);

    if (update.type === 'delete') {
        return items.filter((item) => getKey(item) !== updateKey);
    }

    if (!update.body) {
        return items;
    }

    const existingItem = items.find((item) => getKey(item) === updateKey);

    if (update.type === 'update' && !existingItem) {
        return items;
    }

    const body = new RfidLastSeen(Object.assign({}, existingItem, update.body, {
        deviceId: update.deviceId,
        rfidCode: update.rfidCode
    }));

    const nextItems = [
        body,
        ...items.filter((item) => getKey(item) !== updateKey)
    ];

    return applyLimit(sortRfidLastSeen(nextItems), limit);
};

const updateRfidLastSeenQueries = (queryClient: QueryClient, update: RfidLastSeenUpdate) => {
    updateMatchingQueries(queryClient, [rfidLastSeenQueryKey], (queryKey) => {
        queryClient.setQueryData<RfidLastSeen[]>(queryKey, (items) => {
            return applyRfidLastSeenUpdate(items, update, getDeviceItemKey, getNumberQueryKeyTail(queryKey));
        });
    });

    updateMatchingQueries(queryClient, [rfidLastSeenByDeviceQueryKey, update.deviceId], (queryKey) => {
        queryClient.setQueryData<RfidLastSeen[]>(queryKey, (items) => {
            return applyRfidLastSeenUpdate(items, update, getDeviceItemKey, getNumberQueryKeyTail(queryKey));
        });
    });
};

const subscribeToRfidLastSeenUpdates = (queryClient: QueryClient) => {
    if (subscribed) {
        return;
    }

    OCGatewayClient.socket.on('rfidLastSeenUpdate', (update: RfidLastSeenUpdate) => {
        updateRfidLastSeenQueries(queryClient, update);
    });

    subscribed = true;
};

export function useRfidLastSeenQuery(limit = 50) {
    const queryClient = useQueryClient();

    const query = useQuery<RfidLastSeen[]>({
        queryKey: [rfidLastSeenQueryKey, limit],
        queryFn: async () => {
            const response = await OCGatewayClient.request('getRfidLastSeen', {
                limit,
                subscribe: true
            }) as RfidLastSeen[];

            return applyLimit(dedupeItems(normalizeItems(response), getDeviceItemKey), limit);
        },
        ...gatewayRealtimeQueryOptions
    });

    subscribeToRfidLastSeenUpdates(queryClient);

    return query;
}

export function useHideRfidLastSeenMutation() {
    const queryClient = useQueryClient();

    return useMutation<HideRfidLastSeenResponse, GatewayError, HideRfidLastSeenInput>({
        mutationFn: async (input) => {
            return OCGatewayClient.request('hideRfidLastSeen', input) as Promise<HideRfidLastSeenResponse>;
        },
        onSuccess: (response, variables) => {
            if (!response.hidden) {
                return;
            }

            updateRfidLastSeenQueries(queryClient, {
                type: 'update',
                deviceId: variables.deviceId,
                rfidCode: variables.rfidCode,
                timestamp: new Date(),
                body: {
                    hiddenAt: new Date(),
                    hiddenEventId: variables.eventId
                }
            });
        },
        onError: (error) => {
            if (error.code === 404) {
                queryClient.invalidateQueries({ queryKey: [rfidLastSeenQueryKey] });
                queryClient.invalidateQueries({ queryKey: [rfidLastSeenByDeviceQueryKey] });
            }
        }
    });
}

export function useSetRfidLastSeenLocationMutation() {
    const queryClient = useQueryClient();

    return useMutation<SetRfidLastSeenLocationResponse, GatewayError, SetRfidLastSeenLocationInput>({
        mutationFn: async (input) => {
            return OCGatewayClient.request('setRfidLastSeenLocation', input) as Promise<SetRfidLastSeenLocationResponse>;
        },
        onSuccess: (response) => {
            if (!response.updated || !response.rfidLastSeen) {
                return;
            }

            const rfidLastSeen = new RfidLastSeen(response.rfidLastSeen);
            updateRfidLastSeenQueries(queryClient, {
                type: 'update',
                deviceId: rfidLastSeen.deviceId,
                rfidCode: rfidLastSeen.rfidCode,
                timestamp: new Date(),
                body: rfidLastSeen
            });
        },
        onError: (error) => {
            if (error.code === 404) {
                queryClient.invalidateQueries({ queryKey: [rfidLastSeenQueryKey] });
                queryClient.invalidateQueries({ queryKey: [rfidLastSeenByDeviceQueryKey] });
            }
        }
    });
}

export function useRfidLastSeenByDeviceQuery(deviceId: string, limit = 50) {
    const queryClient = useQueryClient();

    const query = useQuery<RfidLastSeen[]>({
        queryKey: [rfidLastSeenByDeviceQueryKey, deviceId, limit],
        enabled: !!deviceId,
        queryFn: async () => {
            const response = await OCGatewayClient.request('getRfidLastSeenByDevice', {
                deviceId,
                limit,
                subscribe: true
            }) as RfidLastSeen[];

            return applyLimit(dedupeItems(normalizeItems(response), getDeviceItemKey), limit);
        },
        ...gatewayRealtimeQueryOptions
    });

    subscribeToRfidLastSeenUpdates(queryClient);

    return query;
}
